import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { GraduationCap, Loader2, Trash2, Upload, X, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { API } from "../../lib/api";

/**
 * LoRA Identity Training component.
 *
 * Props:
 *  - modelId: string
 *  - modelName: string  (used for default trigger word)
 *  - onStatusChange: fn(status)  parent can refetch model when ready
 */
const LoraTraining = ({ modelId, modelName, onStatusChange }) => {
  const [status, setStatus] = useState({ status: "none" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [files, setFiles] = useState([]);  // [{file, preview}]
  const [steps, setSteps] = useState(1800);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const lastStatusRef = useRef(null);  // null = not initialized yet
  const onStatusChangeRef = useRef(onStatusChange);

  // Keep the latest callback in a ref so fetchStatus / effects don't restart on every parent render
  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/models/${modelId}/lora/status`);
      setStatus(res.data);
      const newStatus = res.data.status;
      // Only notify parent on TRUE TRANSITIONS to ready/failed (not on initial load
      // when status was already terminal — that would cause an infinite parent-refetch loop).
      const isInitial = lastStatusRef.current === null;
      const isTransitionToTerminal =
        !isInitial &&
        (newStatus === "ready" || newStatus === "failed") &&
        lastStatusRef.current !== newStatus;
      lastStatusRef.current = newStatus;
      if (isTransitionToTerminal) {
        onStatusChangeRef.current?.(res.data);
      }
    } catch (e) {
      // 404 means model not found (no-op)
    }
  }, [modelId]);

  // Initial fetch (runs once per modelId)
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Polling interval — only re-creates when training state changes
  useEffect(() => {
    if (status.status !== "training") return undefined;
    const interval = setInterval(fetchStatus, 30000); // every 30s while training
    return () => clearInterval(interval);
  }, [status.status, fetchStatus]);

  const onFilesPicked = (newFiles) => {
    const arr = Array.from(newFiles).filter((f) => f.type.startsWith("image/"));
    const combined = [...files, ...arr].slice(0, 15);
    const previews = combined.map((f) =>
      f.preview ? f : { file: f, preview: URL.createObjectURL(f) }
    );
    setFiles(previews);
  };

  const removeFile = (idx) => {
    const next = [...files];
    next.splice(idx, 1);
    setFiles(next);
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        // Strip data URL prefix → backend handles both anyway
        const b64 = typeof result === "string" ? result.split(",")[1] : "";
        resolve(b64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const startTraining = async () => {
    if (files.length < 4) {
      toast.error("Potrebno je barem 4 slike (preporuka: 8-10)");
      return;
    }
    setSubmitting(true);
    try {
      const images_base64 = await Promise.all(files.map((f) => fileToBase64(f.file)));
      const res = await axios.post(`${API}/models/${modelId}/lora/train`, {
        images_base64,
        steps,
      });
      toast.success(`Treniranje započelo! Trajat će ~${res.data.estimated_minutes} min.`, {
        duration: 6000,
      });
      setDialogOpen(false);
      setFiles([]);
      // Refresh status immediately
      lastStatusRef.current = "none"; // reset so we get a notify on completion
      await fetchStatus();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Trening neuspješan";
      toast.error(typeof msg === "string" ? msg : "Trening neuspješan");
    } finally {
      setSubmitting(false);
    }
  };

  const clearLora = async () => {
    if (!window.confirm("Sigurno želiš obrisati LoRA? Sve nove generacije će raditi bez LoRA-e.")) return;
    try {
      await axios.delete(`${API}/models/${modelId}/lora`);
      toast.success("LoRA uklonjena");
      lastStatusRef.current = "none";
      await fetchStatus();
      onStatusChange?.({ status: "none" });
    } catch (e) {
      toast.error("Greška pri brisanju LoRA-e");
    }
  };

  // ────────────────── RENDER ──────────────────

  // STATUS BADGE — small inline view shown in the model header
  const Badge = () => {
    if (status.status === "ready") {
      return (
        <div
          className="mt-4 p-4 bg-gradient-to-r from-emerald-500/15 to-teal-500/10 rounded-xl border border-emerald-500/30"
          data-testid="lora-status-ready"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              <div>
                <p className="font-medium">🎓 LoRA aktivna — savršen identitet</p>
                <p className="text-text-secondary text-xs mt-1">
                  Sve nove slike koriste tvoj istrenirani LoRA. Lice i tijelo uvijek isti.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={clearLora}
              className="border-white/10 text-text-secondary hover:text-white"
              data-testid="lora-clear-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Obriši
            </Button>
          </div>
        </div>
      );
    }

    if (status.status === "training") {
      const elapsed = status.elapsed_minutes ?? 0;
      const estTotal = 25;  // rough estimate
      const progress = Math.min(95, Math.round((elapsed / estTotal) * 100));
      return (
        <div
          className="mt-4 p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl border border-amber-500/30"
          data-testid="lora-status-training"
        >
          <div className="flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-amber-400 animate-spin shrink-0" />
            <div className="flex-1">
              <p className="font-medium">🎓 LoRA treniranje u tijeku...</p>
              <p className="text-text-secondary text-xs mt-1">
                Proteklo: {elapsed} min &nbsp;•&nbsp; Procjena ukupno: ~{estTotal} min
              </p>
              <div className="mt-2 h-2 bg-surface-highlight rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-500 transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (status.status === "failed") {
      return (
        <div
          className="mt-4 p-4 bg-rose-500/10 rounded-xl border border-rose-500/30"
          data-testid="lora-status-failed"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">LoRA treniranje neuspješno</p>
                <p className="text-text-secondary text-xs mt-1 break-all">
                  {status.error || "Nepoznata greška. Pokušaj opet."}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => setDialogOpen(true)} data-testid="lora-retry-btn">
                Pokušaj ponovo
              </Button>
              <Button variant="outline" size="sm" onClick={clearLora} className="border-white/10">
                Očisti
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // status === "none"
    return (
      <div
        className="mt-4 p-4 bg-surface-highlight rounded-xl border border-border"
        data-testid="lora-status-none"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2">
            <GraduationCap className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Treniraj LoRA za savršen identitet</p>
              <p className="text-text-secondary text-xs mt-1">
                Uploadaj 8-10 slika, treniramo identitetski LoRA (~25 min). Nakon toga
                sve nove generacije imaju 95%+ isti lice i tijelo — uključujući eksplicitne.
              </p>
            </div>
          </div>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-gradient-to-r from-primary to-secondary"
            data-testid="lora-train-btn"
          >
            <GraduationCap className="w-4 h-4 mr-1" />
            Treniraj
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <Badge />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl bg-surface border border-border max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="lora-train-dialog-title">
              <span className="inline-flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-primary" />
                Treniraj LoRA za "{modelName}"
              </span>
            </DialogTitle>
            <DialogDescription>
              Uploadaj 4-15 različitih slika osobe (idealno 8-10). Treniranje radi ~25 min u pozadini.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div
              className="border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-primary transition cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                onFilesPicked(e.dataTransfer.files);
              }}
              data-testid="lora-dropzone"
            >
              <Upload className="w-8 h-8 mx-auto text-primary mb-2" />
              <p className="font-medium">Povuci slike ovdje ili klikni za upload</p>
              <p className="text-text-secondary text-xs mt-1">
                JPG/PNG/WebP &nbsp;•&nbsp; 4-15 slika &nbsp;•&nbsp; idealno 8-10
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => onFilesPicked(e.target.files)}
                data-testid="lora-file-input"
              />
            </div>

            {files.length > 0 && (
              <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                {files.map((f, idx) => (
                  <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-border group">
                    <img src={f.preview} alt={`upload-${idx}`} className="w-full h-full object-cover" />
                    <button
                      onClick={(e) => { e.stopPropagation(); removeFile(idx); }}
                      className="absolute top-1 right-1 bg-black/60 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition"
                      data-testid={`lora-remove-${idx}`}
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="text-xs text-text-secondary px-1">
              Učitano: <span className="font-medium text-white">{files.length}/15</span>
              {files.length < 4 && <span className="text-amber-400"> &nbsp;• potrebno još {4 - files.length}</span>}
              {files.length >= 4 && files.length < 8 && <span className="text-amber-400"> &nbsp;• preporuka 8-10</span>}
              {files.length >= 8 && <span className="text-emerald-400"> &nbsp;• ✓ dovoljno</span>}
            </div>

            <div className="bg-surface-highlight rounded-xl p-3 border border-border">
              <label className="text-sm font-medium block mb-2">Steps treniranja</label>
              <div className="flex gap-2">
                {[1200, 1800, 2500].map((s) => (
                  <button
                    key={s}
                    onClick={() => setSteps(s)}
                    className={`flex-1 py-2 rounded-lg text-sm border ${
                      steps === s ? "bg-primary border-primary text-white" : "border-border text-text-secondary"
                    }`}
                    data-testid={`lora-steps-${s}`}
                  >
                    {s === 1200 ? "Brzo (~15 min)" : s === 1800 ? "Standard (~25 min)" : "Kvaliteta (~35 min)"}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={submitting}
              data-testid="lora-cancel-btn"
            >
              Odustani
            </Button>
            <Button
              onClick={startTraining}
              disabled={submitting || files.length < 4}
              className="bg-gradient-to-r from-primary to-secondary"
              data-testid="lora-start-training-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <GraduationCap className="w-4 h-4 mr-1" />}
              {submitting ? "Šaljem..." : `Treniraj sa ${files.length} slika`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LoraTraining;
